import React from 'react';
import { ChevronDown, Download, FileSpreadsheet, Printer } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { trpc } from '@org/clients/school';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@org/ui';
import {
  buildPrintDocument,
  downloadTextFile,
  exportFileName,
  printHtmlDocument,
  toCsv,
  type ExportTable,
} from './student-export';

type ExportFormat = 'csv' | 'pdf';

/** One row of `users.listStudentsForExport`. */
type ExportStudent = {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  type: string;
  status: string;
  enabled: boolean;
  language: string;
  createdAt: Date | string | null;
  levelName: string | null;
  countryName: string | null;
  stateName: string | null;
  city: string | null;
  dateOfBirth: string | null;
  profession: string | null;
  groups: string[];
};

type ColumnKey =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phone'
  | 'level'
  | 'type'
  | 'status'
  | 'enabled'
  | 'groups'
  | 'country'
  | 'state'
  | 'city'
  | 'dateOfBirth'
  | 'profession'
  | 'createdAt';

// The spreadsheet carries everything; the printed page keeps the columns that
// fit a landscape A4 sheet at a readable size.
const CSV_COLUMNS: ColumnKey[] = [
  'firstName', 'lastName', 'email', 'phone', 'level', 'type', 'status', 'enabled',
  'groups', 'country', 'state', 'city', 'dateOfBirth', 'profession', 'createdAt',
];
const PDF_COLUMNS: ColumnKey[] = [
  'firstName', 'lastName', 'email', 'phone', 'level', 'type', 'status', 'enabled',
  'groups', 'country', 'createdAt',
];
// Share of the printed page width per PDF column, in the same order (sums to 100).
const PDF_COLUMN_WIDTHS = ['9%', '11%', '17%', '10%', '5%', '9%', '8%', '4%', '13%', '7%', '7%'];

// `students.form.type.*` keys use camelCase while the DB value is kebab-case.
const TYPE_LABEL_KEYS: Record<string, string> = {
  group: 'group',
  private: 'private',
  'self-learning': 'selfLearning',
  community: 'community',
  b2b: 'b2b',
};

type StudentExportMenuProps = {
  /** Ids of the students currently shown in the list, in display order. */
  visibleStudentIds: string[];
};

export const StudentExportMenu: React.FC<StudentExportMenuProps> = ({ visibleStudentIds }) => {
  const { t, i18n } = useTranslation('admin');
  const utils = trpc.useUtils();
  const [pending, setPending] = React.useState<ExportFormat | null>(null);

  const dateLocale = i18n.language?.startsWith('pt') ? 'pt-BR' : 'en-US';
  const formatDate = React.useCallback(
    (value: Date | string | null | undefined) => {
      if (!value) return '';
      // Birth dates arrive as `YYYY-MM-DD`; build a local date so the day
      // does not shift with the timezone.
      const date =
        typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
          ? new Date(Number(value.slice(0, 4)), Number(value.slice(5, 7)) - 1, Number(value.slice(8, 10)))
          : new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      return new Intl.DateTimeFormat(dateLocale).format(date);
    },
    [dateLocale],
  );

  const cellFor = React.useCallback(
    (student: ExportStudent, column: ColumnKey): string => {
      switch (column) {
        case 'firstName':
          return student.firstName ?? '';
        case 'lastName':
          return student.lastName ?? '';
        case 'email':
          return student.email ?? '';
        case 'phone':
          return student.phone ?? '';
        case 'level':
          return student.levelName ?? '';
        case 'type': {
          const key = TYPE_LABEL_KEYS[student.type];
          return key ? t(`students.form.type.${key}`) : student.type ?? '';
        }
        case 'status':
          return t(`students.export.status.${student.status}`, { defaultValue: student.status ?? '' });
        case 'enabled':
          return student.enabled ? t('students.export.yes') : t('students.export.no');
        case 'groups':
          return student.groups.join(', ');
        case 'country':
          return student.countryName ?? '';
        case 'state':
          return student.stateName ?? '';
        case 'city':
          return student.city ?? '';
        case 'dateOfBirth':
          return formatDate(student.dateOfBirth);
        case 'profession':
          return student.profession ?? '';
        case 'createdAt':
          return formatDate(student.createdAt);
        default:
          return '';
      }
    },
    [t, formatDate],
  );

  const buildTable = React.useCallback(
    (students: ExportStudent[], columns: ColumnKey[], columnWidths?: string[]): ExportTable => ({
      columnWidths,
      title: t('students.export.title'),
      subtitle: t('students.export.subtitle', {
        count: students.length,
        date: new Intl.DateTimeFormat(dateLocale, { dateStyle: 'short', timeStyle: 'short' }).format(new Date()),
      }),
      headers: columns.map((column) => t(`students.export.columns.${column}`)),
      rows: students.map((student) => columns.map((column) => cellFor(student, column))),
    }),
    [t, dateLocale, cellFor],
  );

  const loadVisibleStudents = async (): Promise<ExportStudent[]> => {
    const all = (await utils.users.listStudentsForExport.fetch()) as ExportStudent[];
    const byId = new Map(all.map((student) => [student.userId, student]));
    return visibleStudentIds
      .map((id) => byId.get(id))
      .filter((student): student is ExportStudent => Boolean(student));
  };

  const exportCsv = async () => {
    setPending('csv');
    try {
      const students = await loadVisibleStudents();
      if (students.length === 0) {
        toast.info(t('students.export.empty'));
        return;
      }
      const table = buildTable(students, CSV_COLUMNS);
      downloadTextFile(
        exportFileName(t('students.export.fileName'), 'csv'),
        toCsv(table),
        'text/csv;charset=utf-8',
      );
    } catch {
      toast.error(t('students.export.error'));
    } finally {
      setPending(null);
    }
  };

  const exportPdf = async () => {
    setPending('pdf');
    try {
      const students = await loadVisibleStudents();
      if (students.length === 0) {
        toast.info(t('students.export.empty'));
        return;
      }
      const table = buildTable(students, PDF_COLUMNS, PDF_COLUMN_WIDTHS);
      printHtmlDocument(buildPrintDocument(table, i18n.language ?? 'pt'));
    } catch {
      toast.error(t('students.export.error'));
    } finally {
      setPending(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" disabled={pending !== null} aria-label={t('students.export.button')}>
          <Download className="h-4 w-4" />
          <span className="sr-only sm:not-sr-only">
            {pending ? t('students.export.preparing') : t('students.export.button')}
          </span>
          <ChevronDown className="h-4 w-4 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuItem onSelect={() => void exportCsv()}>
          <FileSpreadsheet className="mr-2 h-4 w-4" />
          {t('students.export.csv')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void exportPdf()}>
          <Printer className="mr-2 h-4 w-4" />
          {t('students.export.pdf')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
